// Configurable billing amount = f(rate, meter, avgPick, piece).
//
// The admin sets a formula string company-wide (AdminSettings.billingFormula). It is a plain
// arithmetic expression over the whitelisted variables below, evaluated by a small hand-written
// parser — NEVER eval()/Function(), so a saved formula can't run arbitrary code. Anything the
// parser can't understand falls back to DEFAULT_BILLING_FORMULA so billing never breaks.

export const BILLING_VARS = ['rate', 'meter', 'avgPick', 'piece'] as const;
export type BillingVar = typeof BILLING_VARS[number];
export type BillingVars = Record<BillingVar, number>;

// The current hardcoded behaviour — used as the default and the safety-net fallback.
export const DEFAULT_BILLING_FORMULA = 'rate * avgPick * meter';

// One-click choices offered in Settings (the UI also allows a free-form custom formula).
export const BILLING_PRESETS: { label: string; formula: string }[] = [
  { label: 'Rate × Avg Pick × Meter (current)', formula: 'rate * avgPick * meter' },
  { label: 'Rate × Meter', formula: 'rate * meter' },
  { label: 'Rate × Piece', formula: 'rate * piece' },
];

type Token =
  | { type: 'num'; value: number }
  | { type: 'id'; value: string }
  | { type: 'op'; value: '+' | '-' | '*' | '/' }
  | { type: 'lp' }
  | { type: 'rp' };

const isDigit = (c: string) => c >= '0' && c <= '9';
const isIdentStart = (c: string) => (c >= 'a' && c <= 'z') || (c >= 'A' && c <= 'Z') || c === '_';
const isIdentChar = (c: string) => isIdentStart(c) || isDigit(c);

// Break the formula into tokens; throws on any character that isn't a number, identifier,
// arithmetic operator or parenthesis (whitespace is skipped).
const tokenize = (input: string): Token[] => {
  const s = input;
  const tokens: Token[] = [];
  let i = 0;
  while (i < s.length) {
    const c = s[i];
    if (c === ' ' || c === '\t' || c === '\n' || c === '\r') { i++; continue; }
    if (isDigit(c) || c === '.') {
      let j = i + 1;
      while (j < s.length && (isDigit(s[j]) || s[j] === '.')) j++;
      const slice = s.slice(i, j);
      const num = Number(slice);
      if (!isFinite(num)) throw new Error(`Invalid number "${slice}"`);
      tokens.push({ type: 'num', value: num });
      i = j;
    } else if (isIdentStart(c)) {
      let j = i + 1;
      while (j < s.length && isIdentChar(s[j])) j++;
      tokens.push({ type: 'id', value: s.slice(i, j) });
      i = j;
    } else if (c === '+' || c === '-' || c === '*' || c === '/') {
      tokens.push({ type: 'op', value: c });
      i++;
    } else if (c === '(') {
      tokens.push({ type: 'lp' }); i++;
    } else if (c === ')') {
      tokens.push({ type: 'rp' }); i++;
    } else {
      throw new Error(`Unexpected character "${c}"`);
    }
  }
  return tokens;
};

// Recursive-descent parse + evaluate with proper precedence and unary minus:
//   expr   := term (('+' | '-') term)*
//   term   := factor (('*' | '/') factor)*
//   factor := ('-' | '+') factor | number | identifier | '(' expr ')'
// Throws on syntax errors or an identifier not in `vars`.
const parseAndEval = (tokens: Token[], vars: Record<string, number>): number => {
  let pos = 0;
  const peek = () => tokens[pos];
  const eat = () => tokens[pos++];

  const factor = (): number => {
    const t = peek();
    if (!t) throw new Error('Unexpected end of formula');
    if (t.type === 'op' && (t.value === '-' || t.value === '+')) {
      eat();
      const v = factor();
      return t.value === '-' ? -v : v;
    }
    if (t.type === 'num') { eat(); return t.value; }
    if (t.type === 'id') {
      eat();
      if (!(t.value in vars)) throw new Error(`Unknown variable "${t.value}"`);
      return vars[t.value];
    }
    if (t.type === 'lp') {
      eat();
      const v = expr();
      const close = peek();
      if (!close || close.type !== 'rp') throw new Error('Missing ")"');
      eat();
      return v;
    }
    throw new Error('Unexpected token in formula');
  };

  const term = (): number => {
    let v = factor();
    for (;;) {
      const t = peek();
      if (t && t.type === 'op' && (t.value === '*' || t.value === '/')) {
        eat();
        const r = factor();
        v = t.value === '*' ? v * r : (r === 0 ? 0 : v / r); // avoid Infinity/NaN in billing
      } else break;
    }
    return v;
  };

  const expr = (): number => {
    let v = term();
    for (;;) {
      const t = peek();
      if (t && t.type === 'op' && (t.value === '+' || t.value === '-')) {
        eat();
        const r = term();
        v = t.value === '+' ? v + r : v - r;
      } else break;
    }
    return v;
  };

  const result = expr();
  if (pos < tokens.length) throw new Error('Unexpected trailing input in formula');
  return result;
};

// Returns an error message if the formula is empty, malformed, or uses an unknown variable;
// null if it is valid. Used by the Settings UI to block saving a broken formula.
export const validateBillingFormula = (formula: string): string | null => {
  const f = (formula || '').trim();
  if (!f) return 'Formula is empty';
  try {
    const dummy: Record<string, number> = {};
    BILLING_VARS.forEach((v) => { dummy[v] = 1; });
    parseAndEval(tokenize(f), dummy);
    return null;
  } catch (e: any) {
    return e?.message || 'Invalid formula';
  }
};

// Evaluate the formula for one line's variables. Falls back to the default formula (and finally 0)
// so a bad/empty saved formula can never break billing.
export const evalBillingFormula = (formula: string | null | undefined, vars: BillingVars): number => {
  const f = (formula || '').trim() || DEFAULT_BILLING_FORMULA;
  const run = (src: string): number => {
    const v = parseAndEval(tokenize(src), vars as unknown as Record<string, number>);
    return isFinite(v) ? v : 0;
  };
  try {
    return run(f);
  } catch {
    try { return run(DEFAULT_BILLING_FORMULA); } catch { return 0; }
  }
};
